import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  FaMicrophone,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaClock,
  FaUser,
  FaBuilding,
  FaEye,
  FaUsers,
  FaCheckCircle,
  FaHourglassHalf,
  FaEdit,
  FaTimes,
  FaSync,
  FaGlobe,
  FaBullseye,
  FaSpinner,
  FaTrash,
  FaMoneyBillWave,
  FaPercent,
  FaCalculator,
  FaCoins,
  FaTag,
  FaInfoCircle,
  FaFileAlt,
  FaVideo,
  FaCamera,
  FaFilter
} from "react-icons/fa";

const ApprovedPost = () => {
  const [advertisements, setAdvertisements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAdvertisement, setSelectedAdvertisement] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [allReporters, setAllReporters] = useState({});
  const [modifyLoading, setModifyLoading] = useState(false);
  const [showResendModal, setShowResendModal] = useState(false);
  const [selectedReportersForResend, setSelectedReportersForResend] = useState([]);
  const [resendLoading, setResendLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState({});
  const [showAdDetailsModal, setShowAdDetailsModal] = useState(false);
  const [adDetails, setAdDetails] = useState(null);
  const [loadingAdDetails, setLoadingAdDetails] = useState(false);
  const navigate = useNavigate();

  // Fetch approved advertisements
  const fetchApprovedAdvertisements = async (showLoading = false) => {
    try {
      if (showLoading) setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/admin/get/all/ads?limit=1000`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      // The API returns data directly as an array, not wrapped in success/data structure
      const apiData = response.data;
      console.log("📊 Raw API data received:", apiData);

      // Check if apiData is directly an array (which is what the backend returns)
      if (Array.isArray(apiData)) {
        console.log(`📊 Direct array received: ${apiData.length} advertisements`);

        // Debug: Show all statuses
        const allStatuses = apiData.map(ad => ad.status);
        console.log("📊 All advertisement statuses:", allStatuses);
        console.log("📊 Unique statuses:", [...new Set(allStatuses)]);

        // Show ALL advertisements regardless of status - user wants to see all advertisements
        const allAdvertisements = apiData;

        console.log(`📊 All advertisements (no status filtering): ${allAdvertisements.length}`);
        console.log("📊 All advertisements:", allAdvertisements);

        setAdvertisements(allAdvertisements);
        console.log(`📊 State updated with ${allAdvertisements.length} advertisements`);
      } else {
        console.error("API response is not an array:", apiData);
        console.log("Full response structure:", response.data);
        setAdvertisements([]); // Fallback to empty array to prevent errors
        setError("Received invalid data format from server.");
      }
    } catch (error) {
      console.error("Error fetching approved advertisements:", error);
      setError("Failed to load advertisements");
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchApprovedAdvertisements(true); // Show loading on initial load
  }, []);

  // Search/filter states
  const [searchText, setSearchText] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [todayOnly, setTodayOnly] = useState(false);

  // Optionally set to end of day if endOfDay=true
  const toDate = (dateStr, endOfDay = false) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) {
      return null;
    }
    if (endOfDay) {
      d.setHours(23, 59, 59, 999);
    } else {
      d.setHours(0, 0, 0, 0);
    }
    return d;
  };

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 50;

  const filteredAdvertisements = advertisements.filter((advertisement) => {
    // Status filtering is already done in fetchApprovedAdvertisements, so we don't need to filter again
    // if (advertisement.status !== "approved" && advertisement.status !== "modified") return false;

    console.log(`🔍 Filtering advertisement ${advertisement._id} with status: ${advertisement.status}`);

    // Convert advertisement dates to Date objects
    const advertisementDate = toDate(advertisement.startDate);
    const filterStart = toDate(startDate);
    const filterEnd = toDate(endDate, true);

    // Skip advertisements with invalid dates if filters are active
    if ((todayOnly || filterStart || filterEnd) && !advertisementDate) {
      return false;
    }

    if (todayOnly) {
      const today = toDate(new Date());
      if (!advertisementDate || advertisementDate.getTime() !== today.getTime()) {
        return false;
      }
    } else if (filterStart && filterEnd) {
      if (!advertisementDate || advertisementDate < filterStart || advertisementDate > filterEnd) {
        return false;
      }
    } else if (filterStart) {
      if (!advertisementDate || advertisementDate < filterStart) {
        return false;
      }
    } else if (filterEnd) {
      if (!advertisementDate || advertisementDate > filterEnd) {
        return false;
      }
    }

    // Search filter
    if (searchText) {
      const s = searchText.toLowerCase();
      const idMatch = advertisement._id && advertisement._id.toString().toLowerCase().includes(s);
      const topicMatch = advertisement.mediaDescription && advertisement.mediaDescription.toLowerCase().includes(s);
      const stateMatch = advertisement.adState && advertisement.adState.toLowerCase().includes(s);
      const cityMatch = advertisement.adCity && advertisement.adCity.toLowerCase().includes(s);
      const submitterMatch = advertisement.owner?.name && advertisement.owner.name.toLowerCase().includes(s);

      if (!idMatch && !topicMatch && !stateMatch && !cityMatch && !submitterMatch) return false;
    }

    console.log(`✅ Advertisement ${advertisement._id} passed all filters`);
    return true;
  });

  console.log(`📊 Total advertisements in state: ${advertisements.length}`);
  console.log(`📊 Final filtered advertisements count: ${filteredAdvertisements.length}`);
  console.log(`📊 Filtered advertisements:`, filteredAdvertisements.map(a => ({ id: a._id, status: a.status })));



  // Refresh advertisement reporter data (fetch from API)
  const refreshAdvertisementReporters = async (advertisement) => {
    console.log(`🔍 Refreshing reporter data for advertisement ${advertisement._id}`);
    console.log(`🔍 Refresh API URL: ${process.env.REACT_APP_API_BASE_URL}/admin/get/ads/${advertisement._id}/targeted-reporters`);

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/admin/get/ads/${advertisement._id}/targeted-reporters`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      console.log(`📊 Refresh response status:`, response.status);
      console.log(`📊 Refresh response data:`, response.data);

      if (response.data.success) {
        console.log("🔍 Refreshed targeted reporters:", response.data.data);
        setAllReporters(prev => ({
          ...prev,
          [advertisement._id]: response.data.data
        }));
      } else {
        console.error("❌ Failed to refresh targeted reporters:", response.data);
      }
    } catch (error) {
      console.error("❌ Error refreshing targeted reporters:", error);
      if (error.response) {
        console.error("❌ Refresh error response status:", error.response.status);
        console.error("❌ Refresh error response data:", error.response.data);
      }
    }
  };

  // View full advertisement details (same as RunningPost)
  const handleViewAdDetails = async (adId) => {
    setLoadingAdDetails(true);
    setShowAdDetailsModal(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/admin/get/full/advertisement-details/${adId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.success) {
        setAdDetails(response.data.data);
      } else {
        console.error("Failed to fetch ad details:", response.data);
        setShowAdDetailsModal(false);
      }
    } catch (error) {
      console.error("Error fetching ad details:", error);
      setShowAdDetailsModal(false);
    } finally {
      setLoadingAdDetails(false);
    }
  };

  const closeAdDetailsModal = () => {
    setShowAdDetailsModal(false);
    setAdDetails(null);
  };

  // View advertisement details (existing modal with reporters)
  const handleViewDetails = async (advertisement) => {
    console.log(`🔍 Opening PAID advertisement details for: ${advertisement._id}`);
    setSelectedAdvertisement(advertisement);
    setShowModal(true);

    // Fetch targeted reporters from the new API endpoint
    try {
      console.log(`🔍 Fetching targeted reporters for advertisement: ${advertisement._id}`);
      console.log(`🔍 API URL: ${process.env.REACT_APP_API_BASE_URL}/admin/get/ads/${advertisement._id}/targeted-reporters`);

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/admin/get/ads/${advertisement._id}/targeted-reporters`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      console.log(`📊 Advertisement targeted reporters response status:`, response.status);
      console.log(`📊 Advertisement targeted reporters response data:`, response.data);

      if (response.data.success) {
        console.log("🔍 Fetched targeted reporters:", response.data.data);
        console.log("🔍 Setting allReporters state with data:", response.data.data);
        setAllReporters(prev => ({
          ...prev,
          [advertisement._id]: response.data.data
        }));
      } else {
        console.error("❌ API returned success: false:", response.data);
        // Set empty data if API fails
        setAllReporters(prev => ({
          ...prev,
          [advertisement._id]: {
            totalTargetedReporters: 0,
            totalTargetedUsers: 0,
            totalTargetedInfluencers: 0,
            pending: 0,
            accepted: 0,
            rejected: 0,
            completed: 0,
            reporters: [],
            users: []
          }
        }));
      }
    } catch (error) {
      console.error("❌ Error fetching targeted reporters:", error);
      if (error.response) {
        console.error("❌ Error response status:", error.response.status);
        console.error("❌ Error response data:", error.response.data);
        console.error("❌ Error response headers:", error.response.headers);
      } else if (error.request) {
        console.error("❌ No response received:", error.request);
      } else {
        console.error("❌ Request setup error:", error.message);
      }
      // Set empty data if API fails
      setAllReporters(prev => ({
        ...prev,
        [advertisement._id]: {
          totalTargetedReporters: 0,
          totalTargetedUsers: 0,
          totalTargetedInfluencers: 0,
          pending: 0,
          accepted: 0,
          rejected: 0,
          completed: 0,
          reporters: [],
          users: []
        }
      }));
    }
  };

  // Handle modify advertisement
  const handleModifyAdvertisement = (advertisement) => {
    navigate("/admin/advertisement/modify", {
      state: {
        advertisementId: advertisement._id,
        advertisementData: advertisement
      }
    });
  };

  // Handle resend to all reporters
  const handleResendToReporters = async (advertisement) => {
    setModifyLoading(true);
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/admin/modify/ads/${advertisement._id}`,
        {
          action: "modified",
          note: "Resending to all reporters with updated targeting",
          // Keep existing targeting or use default
          allStates: advertisement.allStates || false,
          selectedStates: advertisement.adminSelectState || [],
          adminSelectCities: advertisement.adminSelectCities || [],
          adminSelectPincode: advertisement.adminSelectPincode || "",
          reporterId: advertisement.reporterId || []
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.success) {
        // Refresh the advertisements list
        await fetchApprovedAdvertisements();

        // If modal is open, refresh the reporter data
        if (selectedAdvertisement && selectedAdvertisement._id === advertisement._id) {
          await refreshAdvertisementReporters(advertisement);
        }

        alert("Advertisement resent to all reporters successfully! Rejected reporters' status has been reset to pending.");
      }
    } catch (error) {
      console.error("Error resending to reporters:", error);
    } finally {
      setModifyLoading(false);
    }
  };

  // Handle resend to rejected reporters only
  const handleResendToRejectedReporters = (advertisement) => {
    const advertisementReporters = allReporters[advertisement._id];
    if (advertisementReporters && advertisementReporters.grouped.rejected.length > 0) {
      setSelectedAdvertisement(advertisement);
      setShowResendModal(true);
      // Pre-select all rejected reporters
      setSelectedReportersForResend(advertisementReporters.grouped.rejected.map(r => r.reporterId));
    }
  };

  // Handle resend to selected rejected reporters
  const handleResendToSelectedReporters = async () => {
    if (selectedReportersForResend.length === 0) {
      alert("Please select at least one reporter to resend");
      return;
    }

    setResendLoading(true);
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/admin/modify/ads/${selectedAdvertisement._id}`,
        {
          action: "modified",
          note: `Resending to ${selectedReportersForResend.length} selected rejected reporters`,
          // Use specific reporter targeting for resend
          allStates: false,
          selectedStates: [],
          adminSelectCities: [],
          adminSelectPincode: "",
          reporterId: selectedReportersForResend
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.success) {
        setShowResendModal(false);
        setSelectedReportersForResend([]);

        // Refresh the reporter data for this specific advertisement (force refresh)
        await refreshAdvertisementReporters(selectedAdvertisement);

        // Also refresh the main advertisements list
        await fetchApprovedAdvertisements();

        alert(`Advertisement resent to ${selectedReportersForResend.length} reporters successfully! Their status has been reset to pending.`);
      }
    } catch (error) {
      console.error("Error resending to selected reporters:", error);
    } finally {
      setResendLoading(false);
    }
  };

  // Handle delete reporter from advertisement
  const handleDeleteReporter = async (advertisementId, reporterId, reporterName) => {
    if (!window.confirm(`Are you sure you want to remove ${reporterName} from this advertisement? This action cannot be undone.`)) {
      return;
    }

    setDeleteLoading(prev => ({ ...prev, [reporterId]: true }));

    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/admin/delete/ads/reporter/${advertisementId}/${reporterId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.success) {
        // Refresh the reporter list for this advertisement (force refresh)
        // Find the advertisement in the current list and refresh its data
        const currentAdvertisement = advertisements.find(ad => ad._id === advertisementId);
        if (currentAdvertisement) {
          await refreshAdvertisementReporters(currentAdvertisement);
        }
        alert(`${reporterName} has been removed from the advertisement successfully.`);
      }
    } catch (error) {
      console.error("Error deleting reporter from advertisement:", error);

      // Show specific error message from backend if available
      if (error.response && error.response.data && error.response.data.message) {
        alert(`Failed to remove reporter: ${error.response.data.message}`);
      } else {
        alert("Failed to remove reporter from advertisement. Please try again.");
      }
    } finally {
      setDeleteLoading(prev => ({ ...prev, [reporterId]: false }));
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div className="text-red-500">{error}</div>;

  return (
    <>
      {/* Compact Search/Filter Bar */}
      <div className="w-full sticky top-0 z-30 bg-gradient-to-r from-blue-50 to-indigo-50 shadow-md border-b mb-4 py-3 px-4">
        <div className="w-full max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-3 items-center">
            {/* Compact Date Range Filter */}
            <div className="flex items-center gap-2 bg-white rounded-lg shadow-sm border border-blue-200 px-3 py-2">
              <FaCalendarAlt className="text-blue-600 text-sm" />
              <input
                type="date"
                className="border-0 bg-transparent text-sm focus:ring-0 focus:outline-none w-32"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setTodayOnly(false);
                }}
                max={endDate || undefined}
                placeholder="Start date"
              />
              <span className="text-gray-400 text-sm">→</span>
              <input
                type="date"
                className="border-0 bg-transparent text-sm focus:ring-0 focus:outline-none w-32"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setTodayOnly(false);
                }}
                min={startDate || undefined}
                placeholder="End date"
              />
              <button
                onClick={() => {
                  const today = new Date().toISOString().split('T')[0];
                  setStartDate(today);
                  setEndDate(today);
                  setTodayOnly(true);
                }}
                className={`px-2 py-1 rounded text-xs font-medium transition-colors ${todayOnly
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
              >
                Today
              </button>
            </div>

            {/* Compact Search Input */}
            <div className="flex items-center gap-2 bg-white rounded-lg shadow-sm border border-blue-200 px-3 py-2 flex-1 max-w-md">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
              </svg>
              <input
                type="text"
                placeholder="Search by Conference ID, State, or City..."
                className="flex-1 border-0 bg-transparent text-sm focus:ring-0 focus:outline-none"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
              {searchText && (
                <button
                  onClick={() => setSearchText("")}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              )}
            </div>

            {/* Quick Filter Buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setSearchText("");
                  setStartDate("");
                  setEndDate("");
                  setTodayOnly(false);
                }}
                className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* main content */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
          All Paid Advertisements
        </h2>
        <div className="flex flex-wrap gap-2 sm:space-x-2 sm:space-y-0">
          <button
            className="flex-1 sm:flex-none px-3 py-2 sm:px-4 sm:py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 font-semibold transition-colors text-sm sm:text-base"
            onClick={() => navigate("/admin/pendingposts")}
          >
            Pending
          </button>

          <button
            className="flex-1 sm:flex-none px-3 py-2 sm:px-4 sm:py-2 bg-red-500 text-white rounded hover:bg-red-600 font-semibold transition-colors text-sm sm:text-base"
            onClick={() => navigate("/admin/rejectedposts")}
          >
            Rejected
          </button>
          <button
            className="flex-1 sm:flex-none px-3 py-2 sm:px-4 sm:py-2 bg-blue-500 text-white rounded hover:bg-blue-600 font-semibold transition-colors text-sm sm:text-base"
            onClick={() => navigate("/admin/runningposts")}
          >
            Running
          </button>
          <button
            className="flex-1 sm:flex-none px-3 py-2 sm:px-4 sm:py-2 bg-purple-500 text-white rounded hover:bg-purple-600 font-semibold transition-colors text-sm sm:text-base"
            onClick={() => navigate("/admin/completedposts")}
          >
            Completed
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white rounded-xl shadow-sm border border-gray-100">
          <thead className="bg-gradient-to-r from-blue-50 to-indigo-50">
            <tr>
              <th className="px-2 py-1 text-left text-[10px] font-semibold text-gray-600 uppercase">No.</th>
              <th className="px-2 py-1 text-left text-[10px] font-semibold text-gray-600 uppercase">Ad ID</th>
              <th className="px-2 py-1 text-left text-[10px] font-semibold text-gray-600 uppercase">Date</th>
              <th className="px-2 py-1 text-left text-[10px] font-semibold text-gray-600 uppercase">Location</th>
              <th className="px-2 py-1 text-left text-[10px] font-semibold text-gray-600 uppercase">Platforms</th>
              <th className="px-2 py-1 text-left text-[10px] font-semibold text-gray-600 uppercase">Financial</th>
              <th className="px-2 py-1 text-left text-[10px] font-semibold text-gray-600 uppercase">Targeting</th>
              <th className="px-2 py-1 text-left text-[10px] font-semibold text-gray-600 uppercase">Reporters</th>
              <th className="px-2 py-1 text-left text-[10px] font-semibold text-gray-600 uppercase">Proofs</th>
              <th className="px-2 py-1 text-left text-[10px] font-semibold text-gray-600 uppercase">Status</th>
              <th className="px-2 py-1 text-left text-[10px] font-semibold text-gray-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAdvertisements.length === 0 ? (
              <tr>
                <td colSpan="11" className="text-center py-4">
                  No advertisements found.
                </td>
              </tr>
            ) : (
              (() => {
                const totalPages = Math.ceil(filteredAdvertisements.length / rowsPerPage);
                const paginatedAdvertisements = filteredAdvertisements.slice(
                  (currentPage - 1) * rowsPerPage,
                  currentPage * rowsPerPage
                );
                return paginatedAdvertisements.map((advertisement, index) => (
                  <tr key={advertisement._id} className="border-t hover:bg-gray-50">
                    <td className="px-2 py-1 font-medium text-gray-900 text-xs">{(currentPage - 1) * rowsPerPage + index + 1}</td>
                    <td className="px-2 py-1 font-mono text-[10px] truncate max-w-[100px]">{advertisement._id}</td>
                    <td className="px-2 py-1">
                      <div className="flex items-center text-[10px]">
                        <FaCalendarAlt className="mr-0.5 text-blue-500 w-2 h-2" />
                        {advertisement.startDate}
                      </div>
                      <div className="flex items-center text-[9px] text-gray-500">
                        <FaClock className="mr-0.5 w-2 h-2" />
                        {advertisement.endDate}
                      </div>
                    </td>
                    <td className="px-2 py-1">
                      <div className="flex items-center text-[10px]">
                        <FaMapMarkerAlt className="mr-0.5 text-green-500 w-2 h-2" />
                        {advertisement.pfState || advertisement.adState || "N/A"}
                      </div>
                      {advertisement.pfCities && advertisement.pfCities.length > 0 ? (
                        <div className="text-[9px] text-gray-500 truncate max-w-[100px]">
                          {advertisement.pfCities.slice(0, 1).join(", ")}
                          {advertisement.pfCities.length > 1 && ` +${advertisement.pfCities.length - 1}`}
                        </div>
                      ) : advertisement.adCity ? (
                        <div className="text-[9px] text-gray-500">{advertisement.adCity}</div>
                      ) : (
                        <div className="text-[9px] text-gray-500">N/A</div>
                      )}
                    </td>
                    <td className="px-2 py-1">
                      {advertisement.platforms && advertisement.platforms.length > 0 ? (
                        <div className="flex flex-wrap gap-0.5">
                          {advertisement.platforms.slice(0, 1).map((platform, index) => (
                            <span key={index} className="bg-blue-100 text-blue-800 text-[9px] px-1 py-0.5 rounded capitalize">
                              {platform}
                            </span>
                          ))}
                          {advertisement.platforms.length > 1 && (
                            <span className="bg-gray-100 text-gray-600 text-[9px] px-1 py-0.5 rounded">
                              +{advertisement.platforms.length - 1}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-500 italic text-[9px]">N/A</span>
                      )}
                    </td>
                    <td className="px-2 py-1">
                      <div className="space-y-0.5">
                        <div className="flex items-center gap-0.5">
                          <span className="text-[9px] text-blue-600 font-medium">
                            C: ₹{advertisement.adminCommission ? Number(advertisement.adminCommission).toFixed(0) : "0"}
                          </span>
                        </div>
                        <div className="flex items-center gap-0.5">
                          <span className="text-[9px] text-green-600 font-medium">
                            R: ₹{advertisement.finalReporterPrice ? Number(advertisement.finalReporterPrice).toFixed(0) : "0"}
                          </span>
                        </div>
                        <div className="flex items-center gap-0.5">
                          <span className="text-[9px] text-purple-600 font-medium">
                            {advertisement.requiredReporter || 0} reps
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-2 py-1">
                      <div className="flex items-center text-[10px]">
                        {advertisement.allStates ? (
                          <><FaGlobe className="mr-0.5 text-purple-500 w-2 h-2" />All</>
                        ) : advertisement.adminSelectState?.length > 0 ? (
                          <><FaBullseye className="mr-0.5 text-purple-500 w-2 h-2" />{advertisement.adminSelectState.length}S</>
                        ) : advertisement.adminSelectCities?.length > 0 ? (
                          <><FaBullseye className="mr-0.5 text-purple-500 w-2 h-2" />{advertisement.adminSelectCities.length}C</>
                        ) : advertisement.reporterId?.length > 0 ? (
                          <><FaUsers className="mr-0.5 text-purple-500 w-2 h-2" />{advertisement.reporterId.length}R</>
                        ) : (
                          <><FaMapMarkerAlt className="mr-0.5 text-gray-500 w-2 h-2" />Def</>
                        )}
                      </div>
                      <div className="text-[9px] text-gray-500">
                        {advertisement.status === "modified" ? "Mod" : "Orig"}
                      </div>
                    </td>
                    <td className="px-2 py-1">
                      <div className="flex items-center text-[10px]">
                        <FaUsers className="mr-0.5 text-blue-500 w-2 h-2" />
                        {advertisement.acceptReporterCount || 0} acc
                      </div>
                      <div className="text-[9px] text-gray-500">
                        {allReporters[advertisement._id]?.rejected || 0}rej {allReporters[advertisement._id]?.pending || 0}pen
                      </div>
                    </td>
                    <td className="px-2 py-1">
                      <div className="flex items-center text-[10px]">
                        <FaCheckCircle className="mr-0.5 text-green-500 w-2 h-2" />
                        {advertisement.acceptRejectReporterList?.filter(r => r.adProof).length || 0} sub
                      </div>
                      <div className="text-[9px] text-gray-500">
                        {advertisement.acceptRejectReporterList?.filter(r => r.accepted && !r.adProof).length || 0} pen
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${advertisement.status === "approved"
                        ? "bg-green-100 text-green-800"
                        : advertisement.status === "modified"
                          ? "bg-orange-100 text-orange-800"
                          : advertisement.status === "running"
                            ? "bg-blue-100 text-blue-800"
                            : advertisement.status === "completed"
                              ? "bg-purple-100 text-purple-800"
                              : advertisement.status === "pending"
                                ? "bg-yellow-100 text-yellow-800"
                                : advertisement.status === "rejected"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-gray-100 text-gray-800"
                        }`}>
                        {advertisement.status === "approved" ? "Approved" :
                          advertisement.status === "modified" ? "Modified" :
                            advertisement.status === "running" ? "Running" :
                              advertisement.status === "completed" ? "Completed" :
                                advertisement.status === "pending" ? "Pending" :
                                  advertisement.status === "rejected" ? "Rejected" :
                                    advertisement.status}
                      </span>
                    </td>
                    <td className="px-2 py-1">
                      <div className="flex flex-col gap-0.5">
                        <button
                          className="px-1.5 py-0.5 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center text-[9px]"
                          onClick={() => handleViewDetails(advertisement)}
                        >
                          <FaEye className="mr-0.5 w-2 h-2" />
                          View
                        </button>
                        <button
                          className="px-1.5 py-0.5 bg-indigo-500 text-white rounded hover:bg-indigo-600 flex items-center text-[9px]"
                          onClick={() => handleViewAdDetails(advertisement._id)}
                        >
                          <FaInfoCircle className="mr-0.5 w-2 h-2" />
                          Info
                        </button>
                        <button
                          className="px-1.5 py-0.5 bg-orange-500 text-white rounded hover:bg-orange-600 flex items-center text-[9px]"
                          onClick={() => handleModifyAdvertisement(advertisement)}
                        >
                          <FaEdit className="mr-0.5 w-2 h-2" />
                          Edit
                        </button>
                      </div>
                    </td>
                  </tr>
                ));
              })()
            )}
          </tbody>
        </table>
      </div>
      {/* Pagination Controls */}
      {(() => {
        const totalPages = Math.ceil(filteredAdvertisements.length / rowsPerPage);
        return totalPages > 1 ? (
          <div className="flex justify-center mt-6">
            <nav className="inline-flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className={`px-4 py-1 rounded border border-green-400 text-green-800 bg-white hover:bg-green-100 transition-colors ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                Previous
              </button>
              <span className="px-4 py-1 rounded border border-green-400 bg-green-300 text-green-900 font-bold">
                {currentPage}
              </span>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`px-4 py-1 rounded border border-green-400 text-green-800 bg-white hover:bg-green-100 transition-colors ${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                Next
              </button>
            </nav>
          </div>
        ) : null;
      })()}

      {/* Advertisement Details Modal */}
      {showModal && selectedAdvertisement && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50 transition-all">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-4xl mx-4 text-left relative animate-fadeIn border border-green-200 overflow-y-auto max-h-[90vh]">
            {/* Close button */}
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-3xl font-bold"
              onClick={() => setShowModal(false)}
              aria-label="Close"
            >
              &times;
            </button>

            {/* Header */}
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold mb-2 flex items-center justify-center">
                <FaMicrophone className="mr-2 text-blue-500" />
                {selectedAdvertisement.mediaDescription}
              </h2>
              <span className={`inline-block px-4 py-1 rounded-full text-sm font-semibold ${selectedAdvertisement.status === "approved"
                ? "bg-green-100 text-green-800"
                : selectedAdvertisement.status === "modified"
                  ? "bg-orange-100 text-orange-800"
                  : selectedAdvertisement.status === "running"
                    ? "bg-blue-100 text-blue-800"
                    : selectedAdvertisement.status === "completed"
                      ? "bg-purple-100 text-purple-800"
                      : selectedAdvertisement.status === "pending"
                        ? "bg-yellow-100 text-yellow-800"
                        : selectedAdvertisement.status === "rejected"
                          ? "bg-red-100 text-red-800"
                          : "bg-gray-100 text-gray-800"
                }`}>
                {selectedAdvertisement.status === "approved" ? "Approved" :
                  selectedAdvertisement.status === "modified" ? "Modified" :
                    selectedAdvertisement.status === "running" ? "Running" :
                      selectedAdvertisement.status === "completed" ? "Completed" :
                        selectedAdvertisement.status === "pending" ? "Pending" :
                          selectedAdvertisement.status === "rejected" ? "Rejected" :
                            selectedAdvertisement.status}
              </span>
            </div>

            {/* Advertisement Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-800 mb-3">Advertisement Information</h3>
                <div className="space-y-2 text-sm">
                  <div><strong>Advertisement ID:</strong> <span className="font-mono">{selectedAdvertisement._id}</span></div>
                  <div><strong>Start Date:</strong> {selectedAdvertisement.startDate}</div>
                  <div><strong>End Date:</strong> {selectedAdvertisement.endDate}</div>
                  <div><strong>Media Type:</strong> {selectedAdvertisement.mediaType}</div>
                </div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-green-800 mb-3">Location Details</h3>
                <div className="space-y-2 text-sm">
                  <div><strong>Preference State:</strong> {selectedAdvertisement.pfState || selectedAdvertisement.adState || "N/A"}</div>
                  <div><strong>Preference Cities:</strong> {selectedAdvertisement.pfCities && selectedAdvertisement.pfCities.length > 0 ? selectedAdvertisement.pfCities.join(", ") : (selectedAdvertisement.adCity || "N/A")}</div>
                  <div><strong>State:</strong> {selectedAdvertisement.adState || "N/A"}</div>
                  <div><strong>City:</strong> {selectedAdvertisement.adCity || "N/A"}</div>
                  <div><strong>Pincode:</strong> {selectedAdvertisement.pincode || "N/A"}</div>
                  <div><strong>Required Views:</strong> {selectedAdvertisement.requiredViews}</div>
                </div>
              </div>
            </div>

            {/* Platforms Section */}
            {selectedAdvertisement.platforms && selectedAdvertisement.platforms.length > 0 && (
              <div className="bg-indigo-50 p-4 rounded-lg mb-6 border border-indigo-200">
                <h3 className="text-lg font-semibold text-indigo-800 mb-3 flex items-center">
                  <FaTag className="mr-2" />
                  Platforms
                </h3>
                <div className="flex flex-wrap gap-2">
                  {selectedAdvertisement.platforms.map((platform, index) => (
                    <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
                      {platform}
                    </span>
                  ))}
                </div>
                <p className="text-sm text-indigo-600 mt-2">Platforms where the advertisement should be posted</p>
              </div>
            )}

            {/* Financial Details Section */}
            <div className="bg-gradient-to-br from-emerald-50 to-teal-100 p-6 rounded-2xl border border-emerald-200 mb-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-emerald-500 text-white rounded-lg p-2">
                  <FaCalculator className="w-4 h-4" />
                </div>
                <div>
                  <h5 className="font-semibold text-gray-800">Financial Breakdown</h5>
                  <p className="text-sm text-gray-600">Commission, rewards, and savings analysis</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Total Budget */}
                <div className="bg-white rounded-xl p-4 border border-emerald-200 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <FaMoneyBillWave className="w-4 h-4 text-emerald-600" />
                    <span className="text-xs font-semibold text-gray-500 uppercase">Total Budget</span>
                  </div>
                  <div className="text-2xl font-bold text-emerald-600">₹{selectedAdvertisement.totalCost}</div>
                  <div className="text-xs text-gray-500">Advertiser Payment</div>
                </div>

                {/* Platform Commission */}
                <div className="bg-white rounded-xl p-4 border border-blue-200 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <FaPercent className="w-4 h-4 text-blue-600" />
                    <span className="text-xs font-semibold text-gray-500 uppercase">Platform Commission</span>
                  </div>
                  <div className="text-2xl font-bold text-blue-600">
                    ₹{selectedAdvertisement.adminCommission ? Number(selectedAdvertisement.adminCommission).toFixed(2) : "0.00"}
                  </div>
                  <div className="text-xs text-gray-500">
                    {selectedAdvertisement.adCommissionRate ? `${selectedAdvertisement.adCommissionRate}%` : "0%"} of total
                  </div>
                </div>

                {/* Reporter Rewards */}
                <div className="bg-white rounded-xl p-4 border border-green-200 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <FaCoins className="w-4 h-4 text-green-600" />
                    <span className="text-xs font-semibold text-gray-500 uppercase">Reporter Rewards</span>
                  </div>
                  <div className="text-2xl font-bold text-green-600">
                    ₹{selectedAdvertisement.finalReporterPrice ? Number(selectedAdvertisement.finalReporterPrice).toFixed(2) : "0.00"}
                  </div>
                  <div className="text-xs text-gray-500">
                    Per reporter ({selectedAdvertisement.requiredReporter || 0} reporters)
                  </div>
                </div>

                {/* Total Reporter Budget */}
                <div className="bg-white rounded-xl p-4 border border-purple-200 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <FaUser className="w-4 h-4 text-purple-600" />
                    <span className="text-xs font-semibold text-gray-500 uppercase">Total Reporter Budget</span>
                  </div>
                  <div className="text-2xl font-bold text-purple-600">
                    ₹{selectedAdvertisement.finalReporterPrice && selectedAdvertisement.requiredReporter
                      ? (Number(selectedAdvertisement.finalReporterPrice) * Number(selectedAdvertisement.requiredReporter)).toFixed(2)
                      : "0.00"}
                  </div>
                  <div className="text-xs text-gray-500">
                    {selectedAdvertisement.requiredReporter || 0} reporters × ₹{selectedAdvertisement.finalReporterPrice || "0.00"}
                  </div>
                </div>
              </div>

              {/* Detailed Breakdown */}
              <div className="mt-6 bg-white rounded-xl p-4 border border-gray-200">
                <h6 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <FaCalculator className="w-4 h-4 text-gray-600" />
                  Detailed Financial Analysis
                </h6>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Advertisement Cost:</span>
                      <span className="font-medium">₹{selectedAdvertisement.totalCost}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Platform Commission ({selectedAdvertisement.adCommissionRate || 0}%):</span>
                      <span className="font-medium text-blue-600">₹{selectedAdvertisement.adminCommission ? Number(selectedAdvertisement.adminCommission).toFixed(2) : "0.00"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Reporter Budget:</span>
                      <span className="font-medium text-green-600">
                        ₹{selectedAdvertisement.finalReporterPrice && selectedAdvertisement.requiredReporter
                          ? (Number(selectedAdvertisement.finalReporterPrice) * Number(selectedAdvertisement.requiredReporter)).toFixed(2)
                          : "0.00"}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Required Views:</span>
                      <span className="font-medium">{selectedAdvertisement.requiredViews?.toLocaleString() || "0"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Base View per Reporter:</span>
                      <span className="font-medium">{selectedAdvertisement.baseView || "N/A"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Views per Reporter:</span>
                      <span className="font-medium text-purple-600">
                        {selectedAdvertisement.baseView ? Math.ceil((selectedAdvertisement.requiredViews || 0) / selectedAdvertisement.baseView) : "N/A"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Savings Analysis */}
              <div className="mt-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-4 border border-yellow-200">
                <h6 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                  <FaCoins className="w-4 h-4" />
                  Cost Efficiency Analysis
                </h6>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div className="text-center">
                    <div className="text-lg font-bold text-yellow-700">
                      ₹{selectedAdvertisement.finalReporterPrice ? (Number(selectedAdvertisement.finalReporterPrice) / (selectedAdvertisement.baseView || 1)).toFixed(4) : "0.0000"}
                    </div>
                    <div className="text-xs text-yellow-600">Cost per View</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-orange-700">
                      {selectedAdvertisement.requiredReporter || 0}
                    </div>
                    <div className="text-xs text-orange-600">Reporters Needed</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-green-700">
                      {selectedAdvertisement.adCommissionRate ? (100 - selectedAdvertisement.adCommissionRate).toFixed(1) : "0"}%
                    </div>
                    <div className="text-xs text-green-600">Reporter Share</div>
                  </div>
                </div>
              </div>
            </div>

            {/* All Users Who Have Advertisement in Their Panel */}
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">All Users Who Have This Advertisement in Their Panel</h3>
              <p className="text-sm text-gray-600 mb-4">
                These are all the users (reporters and influencers) who received notifications and have this advertisement available in their panel to accept or reject.
              </p>

              {allReporters[selectedAdvertisement._id] ? (
                <>

                  {/* User Statistics - Compact with Highlights */}
                  <div className="grid grid-cols-2 md:grid-cols-7 gap-2 mb-4">
                    <div className="text-center bg-gradient-to-br from-gray-50 to-gray-100 p-2 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                      <div className="text-xl font-bold text-gray-700">{allReporters[selectedAdvertisement._id].totalTargetedUsers || allReporters[selectedAdvertisement._id].totalTargetedReporters}</div>
                      <div className="text-xs text-gray-600 font-medium">Total Users</div>
                    </div>
                    <div className="text-center bg-gradient-to-br from-purple-50 to-purple-100 p-2 rounded-lg border border-purple-200 shadow-sm hover:shadow-md transition-shadow">
                      <div className="text-xl font-bold text-purple-700">{allReporters[selectedAdvertisement._id].totalTargetedReporters || 0}</div>
                      <div className="text-xs text-purple-600 font-medium">Reporters</div>
                    </div>
                    <div className="text-center bg-gradient-to-br from-pink-50 to-pink-100 p-2 rounded-lg border border-pink-200 shadow-sm hover:shadow-md transition-shadow">
                      <div className="text-xl font-bold text-pink-700">{allReporters[selectedAdvertisement._id].totalTargetedInfluencers || 0}</div>
                      <div className="text-xs text-pink-600 font-medium">Influencers</div>
                    </div>
                    <div className="text-center bg-gradient-to-br from-yellow-50 to-yellow-100 p-2 rounded-lg border border-yellow-200 shadow-sm hover:shadow-md transition-shadow">
                      <div className="text-xl font-bold text-yellow-700">{allReporters[selectedAdvertisement._id].pending}</div>
                      <div className="text-xs text-yellow-600 font-medium">Pending</div>
                    </div>
                    <div className="text-center bg-gradient-to-br from-green-50 to-green-100 p-2 rounded-lg border border-green-200 shadow-sm hover:shadow-md transition-shadow">
                      <div className="text-xl font-bold text-green-700">{allReporters[selectedAdvertisement._id].accepted}</div>
                      <div className="text-xs text-green-600 font-medium">Accepted</div>
                    </div>
                    <div className="text-center bg-gradient-to-br from-red-50 to-red-100 p-2 rounded-lg border border-red-200 shadow-sm hover:shadow-md transition-shadow">
                      <div className="text-xl font-bold text-red-700">{allReporters[selectedAdvertisement._id].rejected}</div>
                      <div className="text-xs text-red-600 font-medium">Rejected</div>
                    </div>
                    <div className="text-center bg-gradient-to-br from-blue-50 to-blue-100 p-2 rounded-lg border border-blue-200 shadow-sm hover:shadow-md transition-shadow">
                      <div className="text-xl font-bold text-blue-700">{allReporters[selectedAdvertisement._id].completed || 0}</div>
                      <div className="text-xs text-blue-600 font-medium">Completed</div>
                    </div>
                  </div>

                  {/* Rejected Users Section */}
                  {allReporters[selectedAdvertisement._id].rejected > 0 && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-red-600 font-semibold text-sm">⚠️ Rejected Users ({allReporters[selectedAdvertisement._id].rejected})</span>
                      </div>
                      <p className="text-red-700 text-xs">
                        The following users have rejected this advertisement. Review their reasons below.
                      </p>
                    </div>
                  )}

                  {/* All Users Table - Compact with Highlights */}
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white rounded-lg shadow-sm border border-gray-200">
                      <thead>
                        <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                          <th className="px-2 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">IINSAF ID</th>
                          <th className="px-2 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Name</th>
                          <th className="px-2 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Email</th>
                          <th className="px-2 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Location</th>
                          <th className="px-2 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">User Type</th>
                          <th className="px-2 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Status</th>
                          <th className="px-2 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Date</th>
                          <th className="px-2 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Notes</th>
                          <th className="px-2 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(allReporters[selectedAdvertisement._id].users || allReporters[selectedAdvertisement._id].reporters).map((user, index) => (
                          <tr key={index} className={`border-b border-gray-100 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-200 ${user.status === "completed" ? "bg-gradient-to-r from-green-50 to-emerald-50" :
                            user.status === "submitted" ? "bg-gradient-to-r from-purple-50 to-violet-50" :
                              user.status === "accepted" ? "bg-gradient-to-r from-blue-50 to-cyan-50" :
                                user.status === "rejected" ? "bg-gradient-to-r from-red-50 to-pink-50" :
                                  "bg-white"
                            }`}>
                            <td className="px-2 py-2 text-xs font-mono font-semibold text-gray-800">{user.iinsafId || user.userId || "N/A"}</td>
                            <td className="px-2 py-2 text-xs font-semibold text-gray-800">{user.userName || user.reporterName || "N/A"}</td>
                            <td className="px-2 py-2 text-xs text-gray-600 truncate max-w-32">{user.userEmail || user.reporterEmail || "N/A"}</td>
                            <td className="px-2 py-2 text-xs text-gray-600">{user.userCity || user.reporterCity}, {user.userState || user.reporterState}</td>
                            <td className="px-2 py-2 text-xs">
                              <span className={`px-2 py-1 rounded-full text-xs font-bold shadow-sm ${user.userType === "Influencer"
                                ? "bg-gradient-to-r from-pink-500 to-pink-600 text-white shadow-pink-200"
                                : "bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-purple-200"
                                }`}>
                                {user.userType || "Reporter"}
                              </span>
                            </td>
                            <td className="px-2 py-2 text-xs">
                              <span className={`px-2 py-1 rounded-full text-xs font-bold shadow-sm ${user.status === "accepted"
                                ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-blue-200"
                                : user.status === "submitted"
                                  ? "bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-purple-200"
                                  : user.status === "rejected"
                                    ? "bg-gradient-to-r from-red-500 to-red-600 text-white shadow-red-200"
                                    : user.status === "completed"
                                      ? "bg-gradient-to-r from-green-500 to-green-600 text-white shadow-green-200"
                                      : "bg-gradient-to-r from-yellow-500 to-yellow-600 text-white shadow-yellow-200"
                                }`}>
                                {user.status === "accepted" ? "Accepted" :
                                  user.status === "submitted" ? "Submitted" :
                                    user.status === "rejected" ? "Rejected" :
                                      user.status === "completed" ? "Completed" : "Pending"}
                              </span>
                            </td>
                            <td className="px-2 py-2 text-xs text-gray-600">
                              {user.status === "completed" && user.completedAt ?
                                new Date(user.completedAt).toLocaleDateString('en-IN') :
                                user.status === "submitted" && user.submittedAt ?
                                  new Date(user.submittedAt).toLocaleDateString('en-IN') :
                                  user.acceptedAt ? new Date(user.acceptedAt).toLocaleDateString('en-IN') :
                                    user.rejectedAt ? new Date(user.rejectedAt).toLocaleDateString('en-IN') :
                                      "No response"}
                            </td>
                            <td className="px-2 py-2 text-xs">
                              {user.status === "completed" ? (
                                <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full font-semibold">Proof approved</span>
                              ) : user.status === "submitted" ? (
                                <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full font-semibold">Proof submitted</span>
                              ) : user.rejectNote ? (
                                <div className="space-y-1">
                                  <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full font-semibold">Rejected</span>
                                  <div className="text-red-700 text-xs bg-red-50 p-1 rounded border border-red-200">
                                    <strong>Reason:</strong> {user.rejectNote}
                                  </div>
                                </div>
                              ) : user.proofSubmitted ? (
                                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full font-semibold">Proof submitted</span>
                              ) : (
                                <span className="text-gray-400">No notes</span>
                              )}
                            </td>
                            <td className="px-2 py-2 text-xs">
                              {user.status === "pending" || user.status === "submitted" ? (
                                <span className="text-gray-400 text-xs font-medium">No action</span>
                              ) : (
                                <button
                                  onClick={() => handleDeleteReporter(selectedAdvertisement._id, user.userId || user.reporterId, user.userName || user.reporterName)}
                                  disabled={deleteLoading[user.userId || user.reporterId]}
                                  className={`px-2 py-1 rounded-md text-xs font-bold transition-all duration-200 flex items-center shadow-sm ${deleteLoading[user.userId || user.reporterId]
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700 shadow-red-200 hover:shadow-md'
                                    }`}
                                  title="Remove user from advertisement (only available for users who have responded)"
                                >
                                  {deleteLoading[user.userId || user.reporterId] ? (
                                    <>
                                      <FaSpinner className="animate-spin mr-1" />
                                      Removing...
                                    </>
                                  ) : (
                                    <>
                                      <FaTrash className="mr-1" />
                                      Remove
                                    </>
                                  )}
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <div className="text-gray-500 mb-4">
                    <FaUsers className="mx-auto text-4xl mb-2" />
                    <p>No user data available</p>
                    <p className="text-sm">This advertisement may not have any targeted users yet.</p>
                  </div>
                  <button
                    onClick={async () => await refreshAdvertisementReporters(selectedAdvertisement)}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center mx-auto"
                  >
                    <FaSync className="mr-2" />
                    Refresh User Data
                  </button>
                </div>
              )}
            </div>

            {/* Admin Targeting Information */}
            <div className="bg-purple-50 p-4 rounded-lg mb-6">
              <h3 className="text-lg font-semibold text-purple-800 mb-3">Admin Targeting Configuration</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <>
                  <div><strong>All States:</strong> {selectedAdvertisement.allStates ? "Yes" : "No"}</div>
                  <div><strong>Selected States:</strong> {selectedAdvertisement.adminSelectState?.join(", ") || "None"}</div>
                  <div><strong>Selected Cities:</strong> {selectedAdvertisement.adminSelectCities?.join(", ") || "None"}</div>
                  <div><strong>Selected Pincode:</strong> {selectedAdvertisement.adminSelectPincode || "None"}</div>
                  <div><strong>Target User Type:</strong>
                    <span className={`px-2 py-1 rounded-full text-xs font-bold ml-2 ${selectedAdvertisement.userType === "influencer"
                      ? "bg-gradient-to-r from-pink-500 to-pink-600 text-white"
                      : selectedAdvertisement.userType === "both"
                        ? "bg-gradient-to-r from-purple-500 to-purple-600 text-white"
                        : "bg-gradient-to-r from-blue-500 to-blue-600 text-white"
                      }`}>
                      {selectedAdvertisement.userType === "influencer" ? "Influencers Only" :
                        selectedAdvertisement.userType === "both" ? "Both Reporters & Influencers" : "Reporters Only"}
                    </span>
                  </div>
                  <div><strong>Total Targeted Users:</strong> {allReporters[selectedAdvertisement._id]?.totalTargetedUsers || allReporters[selectedAdvertisement._id]?.totalTargetedReporters || 0} users</div>
                  <div><strong>Total Reporters:</strong> {allReporters[selectedAdvertisement._id]?.totalTargetedReporters || 0} reporters</div>
                  <div><strong>Total Influencers:</strong> {allReporters[selectedAdvertisement._id]?.totalTargetedInfluencers || 0} influencers</div>
                  <div><strong>Targeting Method:</strong> {selectedAdvertisement.reporterId?.length > 0 ? "Specific Reporter Selection" :
                    selectedAdvertisement.allStates ? "All States" :
                      selectedAdvertisement.adminSelectState?.length > 0 ? "Selected States/Cities" :
                        "Location-based (State/City)"}</div>
                  <div><strong>Modified At:</strong> {selectedAdvertisement.updatedAt ? new Date(selectedAdvertisement.updatedAt).toLocaleString('en-IN') : "N/A"}</div>
                </>
              </div>
            </div>


            {/* Admin Actions */}
            <div className="bg-yellow-50 p-4 rounded-lg mb-6">
              <h3 className="text-lg font-semibold text-yellow-800 mb-3">Admin Actions</h3>
              <div className="flex flex-wrap gap-3">
                <button
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center"
                  onClick={() => handleModifyAdvertisement(selectedAdvertisement)}
                >
                  <FaEdit className="mr-2" />
                  Modify Targeting
                </button>
                <button
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center"
                  onClick={() => setShowModal(false)}
                >
                  <FaEye className="mr-2" />
                  Close Details
                </button>
              </div>
            </div>

            {/* Submitter Information */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Submitted By</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div><strong>Name:</strong> {selectedAdvertisement.owner?.name || "N/A"}</div>
                <div><strong>Organization:</strong> {selectedAdvertisement.owner?.organization || "N/A"}</div>
                <div><strong>Email:</strong> {selectedAdvertisement.owner?.email || "N/A"}</div>
                <div><strong>Advertiser ID:</strong> {selectedAdvertisement.owner?._id || "N/A"}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Resend to Reporters Modal */}
      {showResendModal && selectedAdvertisement && allReporters[selectedAdvertisement._id] && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50 transition-all">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-4xl mx-4 text-left relative animate-fadeIn border border-blue-200 overflow-y-auto max-h-[90vh]">
            {/* Close button */}
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-3xl font-bold"
              onClick={() => setShowResendModal(false)}
              aria-label="Close"
            >
              &times;
            </button>

            {/* Header */}
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold mb-2 flex items-center justify-center">
                <FaSync className="mr-2 text-blue-500" />
                Resend Advertisement to Users
              </h2>
              <p className="text-gray-600">Select which users to resend the advertisement to</p>
            </div>

            {/* Advertisement Info */}
            <div className="bg-blue-50 p-4 rounded-lg mb-6">
              <h3 className="text-lg font-semibold text-blue-800 mb-2">Advertisement: {selectedAdvertisement.mediaDescription}</h3>
              <p className="text-sm text-blue-700">Advertisement ID: {selectedAdvertisement._id}</p>
            </div>

            {/* User Status Summary - Compact with Highlights */}
            <div className="grid grid-cols-2 md:grid-cols-7 gap-2 mb-4">
              <div className="text-center bg-gradient-to-br from-gray-50 to-gray-100 p-2 rounded-lg border border-gray-200 shadow-sm">
                <div className="text-lg font-bold text-gray-700">{allReporters[selectedAdvertisement._id].totalTargetedUsers || allReporters[selectedAdvertisement._id].totalTargetedReporters}</div>
                <div className="text-xs text-gray-600 font-medium">Total Users</div>
              </div>
              <div className="text-center bg-gradient-to-br from-purple-50 to-purple-100 p-2 rounded-lg border border-purple-200 shadow-sm">
                <div className="text-lg font-bold text-purple-700">{allReporters[selectedAdvertisement._id].totalTargetedReporters || 0}</div>
                <div className="text-xs text-purple-600 font-medium">Reporters</div>
              </div>
              <div className="text-center bg-gradient-to-br from-pink-50 to-pink-100 p-2 rounded-lg border border-pink-200 shadow-sm">
                <div className="text-lg font-bold text-pink-700">{allReporters[selectedAdvertisement._id].totalTargetedInfluencers || 0}</div>
                <div className="text-xs text-pink-600 font-medium">Influencers</div>
              </div>
              <div className="text-center bg-gradient-to-br from-yellow-50 to-yellow-100 p-2 rounded-lg border border-yellow-200 shadow-sm">
                <div className="text-lg font-bold text-yellow-700">{allReporters[selectedAdvertisement._id].pending}</div>
                <div className="text-xs text-yellow-600 font-medium">Pending</div>
              </div>
              <div className="text-center bg-gradient-to-br from-green-50 to-green-100 p-2 rounded-lg border border-green-200 shadow-sm">
                <div className="text-lg font-bold text-green-700">{allReporters[selectedAdvertisement._id].accepted}</div>
                <div className="text-xs text-green-600 font-medium">Accepted</div>
              </div>
              <div className="text-center bg-gradient-to-br from-red-50 to-red-100 p-2 rounded-lg border border-red-200 shadow-sm">
                <div className="text-lg font-bold text-red-700">{allReporters[selectedAdvertisement._id].rejected}</div>
                <div className="text-xs text-red-600 font-medium">Rejected</div>
              </div>
              <div className="text-center bg-gradient-to-br from-blue-50 to-blue-100 p-2 rounded-lg border border-blue-200 shadow-sm">
                <div className="text-lg font-bold text-blue-700">{allReporters[selectedAdvertisement._id].completed || 0}</div>
                <div className="text-xs text-blue-600 font-medium">Completed</div>
              </div>
            </div>

            {/* All Users List */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                All Users Who Have Advertisement in Panel ({allReporters[selectedAdvertisement._id].totalTargetedUsers || allReporters[selectedAdvertisement._id].totalTargetedReporters})
              </h3>

              <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-lg">
                {(allReporters[selectedAdvertisement._id].users || allReporters[selectedAdvertisement._id].reporters).map((user, index) => (
                  <div key={index} className={`flex items-center p-2 border-b border-gray-100 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-200 ${user.status === "completed" ? "bg-gradient-to-r from-green-50 to-emerald-50" :
                    user.status === "accepted" ? "bg-gradient-to-r from-blue-50 to-cyan-50" :
                      user.status === "rejected" ? "bg-gradient-to-r from-red-50 to-pink-50" :
                        "bg-white"
                    }`}>
                    <input
                      type="checkbox"
                      checked={selectedReportersForResend.includes(user.userId || user.reporterId)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedReportersForResend(prev => [...prev, user.userId || user.reporterId]);
                        } else {
                          setSelectedReportersForResend(prev => prev.filter(id => id !== (user.userId || user.reporterId)));
                        }
                      }}
                      className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-semibold text-gray-900 text-sm">{user.userName || user.reporterName}</div>
                          <div className="text-xs text-gray-500 truncate max-w-48">{user.userEmail || user.reporterEmail}</div>
                          <div className="text-xs text-gray-500">{user.userCity || user.reporterCity}, {user.userState || user.reporterState}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs font-mono text-gray-600 font-semibold">{user.iinsafId || user.userId}</div>
                          <div className="text-xs mt-1">
                            <span className={`px-2 py-1 rounded-full text-xs font-bold shadow-sm mr-1 ${user.userType === "Influencer"
                              ? "bg-gradient-to-r from-pink-500 to-pink-600 text-white shadow-pink-200"
                              : "bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-purple-200"
                              }`}>
                              {user.userType || "Reporter"}
                            </span>
                            <span className={`px-2 py-1 rounded-full text-xs font-bold shadow-sm ${user.status === "accepted"
                              ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-blue-200"
                              : user.status === "submitted"
                                ? "bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-purple-200"
                                : user.status === "rejected"
                                  ? "bg-gradient-to-r from-red-500 to-red-600 text-white shadow-red-200"
                                  : user.status === "completed"
                                    ? "bg-gradient-to-r from-green-500 to-green-600 text-white shadow-green-200"
                                    : "bg-gradient-to-r from-yellow-500 to-yellow-600 text-white shadow-yellow-200"
                              }`}>
                              {user.status === "accepted" ? "Accepted" :
                                user.status === "submitted" ? "Submitted" :
                                  user.status === "rejected" ? "Rejected" :
                                    user.status === "completed" ? "Completed" : "Pending"}
                            </span>
                          </div>
                          {user.status === "rejected" && user.rejectedAt && (
                            <div className="text-xs text-red-600 mt-1">
                              {new Date(user.rejectedAt).toLocaleDateString('en-IN')}
                            </div>
                          )}
                          {user.status === "accepted" && user.acceptedAt && (
                            <div className="text-xs text-green-600 mt-1">
                              {new Date(user.acceptedAt).toLocaleDateString('en-IN')}
                            </div>
                          )}
                          {user.status === "completed" && user.completedAt && (
                            <div className="text-xs text-blue-600 mt-1">
                              {new Date(user.completedAt).toLocaleDateString('en-IN')}
                            </div>
                          )}
                        </div>
                      </div>
                      {user.rejectNote && (
                        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                          <div className="flex items-center gap-1 mb-1">
                            <span className="text-red-600 font-semibold text-xs">❌ Rejection Reason:</span>
                          </div>
                          <div className="text-red-700 text-xs bg-white p-2 rounded border border-red-100">
                            {user.rejectNote}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Selection Summary */}
            <div className="bg-blue-50 p-4 rounded-lg mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-blue-800">Selected Users</h4>
                  <p className="text-sm text-blue-600">
                    {selectedReportersForResend.length} of {allReporters[selectedAdvertisement._id].totalTargetedUsers || allReporters[selectedAdvertisement._id].totalTargetedReporters} users selected
                  </p>
                  <div className="text-xs text-gray-600 mt-1">
                    Selected: {selectedReportersForResend.length} |
                    Pending: {(allReporters[selectedAdvertisement._id].users || allReporters[selectedAdvertisement._id].reporters).filter(u => selectedReportersForResend.includes(u.userId || u.reporterId) && u.status === "pending").length} |
                    Accepted: {(allReporters[selectedAdvertisement._id].users || allReporters[selectedAdvertisement._id].reporters).filter(u => selectedReportersForResend.includes(u.userId || u.reporterId) && u.status === "accepted").length} |
                    Rejected: {(allReporters[selectedAdvertisement._id].users || allReporters[selectedAdvertisement._id].reporters).filter(u => selectedReportersForResend.includes(u.userId || u.reporterId) && u.status === "rejected").length} |
                    Completed: {(allReporters[selectedAdvertisement._id].users || allReporters[selectedAdvertisement._id].reporters).filter(u => selectedReportersForResend.includes(u.userId || u.reporterId) && u.status === "completed").length}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setSelectedReportersForResend(
                      (allReporters[selectedAdvertisement._id].users || allReporters[selectedAdvertisement._id].reporters).map(u => u.userId || u.reporterId)
                    )}
                    className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
                  >
                    Select All
                  </button>
                  <button
                    onClick={() => setSelectedReportersForResend(
                      (allReporters[selectedAdvertisement._id].users || allReporters[selectedAdvertisement._id].reporters).filter(u => u.status === "rejected").map(u => u.userId || u.reporterId)
                    )}
                    className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600"
                  >
                    Select Rejected Only
                  </button>
                  <button
                    onClick={() => setSelectedReportersForResend([])}
                    className="px-3 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600"
                  >
                    Clear All
                  </button>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowResendModal(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleResendToSelectedReporters}
                disabled={selectedReportersForResend.length === 0 || resendLoading}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {resendLoading ? (
                  <>
                    <FaSpinner className="mr-2 animate-spin" />
                    Resending...
                  </>
                ) : (
                  <>
                    <FaSync className="mr-2" />
                    Resend to {selectedReportersForResend.length} Users
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Advertisement Full Details Modal */}
      {showAdDetailsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-y-auto relative border border-gray-200">
            {/* Header Section */}
            <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-700 text-white p-6 rounded-t-2xl relative">
              <button
                className="absolute top-4 right-4 text-white hover:text-gray-200 text-2xl font-bold bg-white bg-opacity-20 rounded-full w-8 h-8 flex items-center justify-center transition-colors"
                onClick={closeAdDetailsModal}
                title="Close"
              >
                &times;
              </button>
              <div className="flex items-center gap-3">
                <div className="bg-white bg-opacity-20 rounded-lg p-2">
                  <FaInfoCircle className="w-6 h-6 text-indigo-300" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">
                    Full Advertisement Details
                  </h3>
                  <p className="text-indigo-100 text-sm">
                    Complete information submitted by advertiser
                  </p>
                </div>
              </div>
            </div>

            <div className="p-8">
              {loadingAdDetails ? (
                <div className="flex justify-center items-center h-64">
                  <div className="flex flex-col items-center gap-4">
                    <div className="relative w-16 h-16">
                      <div className="absolute inset-0 rounded-full border-4 border-transparent border-r-indigo-500 animate-spin"></div>
                      <div className="absolute inset-2 rounded-full border-4 border-transparent border-r-purple-500 animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
                    </div>
                    <p className="text-gray-600 font-medium">Loading advertisement details...</p>
                  </div>
                </div>
              ) : adDetails ? (
                <>
                  {/* Information Cards Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                    {/* Basic Information Card */}
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-2xl border border-blue-200">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-blue-500 text-white rounded-lg p-2">
                          <FaFileAlt className="w-4 h-4" />
                        </div>
                        <div>
                          <h5 className="font-semibold text-gray-800">Basic Information</h5>
                          <p className="text-sm text-gray-600">Advertisement details</p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="bg-white rounded-lg p-3 border border-blue-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Ad ID</span>
                          <span className="font-mono text-sm text-gray-900 break-all">{adDetails._id}</span>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-blue-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Ad Type</span>
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                            {adDetails.adType || "N/A"}
                          </span>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-blue-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Media Type</span>
                          <span className="font-medium text-gray-900">{adDetails.mediaType || "N/A"}</span>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-blue-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Media Description</span>
                          <span className="font-medium text-gray-900">{adDetails.mediaDescription || "N/A"}</span>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-blue-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Ad Length</span>
                          <span className="font-medium text-gray-900">{adDetails.adLength || "N/A"}</span>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-blue-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Status</span>
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${adDetails.status === "approved" ? "bg-green-100 text-green-800" :
                            adDetails.status === "rejected" ? "bg-red-100 text-red-800" :
                              adDetails.status === "running" ? "bg-blue-100 text-blue-800" :
                                adDetails.status === "completed" ? "bg-purple-100 text-purple-800" :
                                  "bg-yellow-100 text-yellow-800"
                            }`}>
                            {adDetails.status || "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Campaign Details Card */}
                    <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-2xl border border-green-200">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-green-500 text-white rounded-lg p-2">
                          <FaCalendarAlt className="w-4 h-4" />
                        </div>
                        <div>
                          <h5 className="font-semibold text-gray-800">Campaign Details</h5>
                          <p className="text-sm text-gray-600">Timeline and requirements</p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="bg-white rounded-lg p-3 border border-green-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Start Date</span>
                          <span className="font-medium text-gray-900">{adDetails.startDate || "N/A"}</span>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-green-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">End Date</span>
                          <span className="font-medium text-gray-900">{adDetails.endDate || "N/A"}</span>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-green-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Required Views</span>
                          <span className="text-xl font-bold text-green-600">
                            {adDetails.requiredViews ? Number(adDetails.requiredViews).toLocaleString() : "N/A"}
                          </span>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-green-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Required Reporters</span>
                          <span className="text-lg font-bold text-green-600">
                            {adDetails.requiredReporter || "N/A"}
                          </span>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-green-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Base View</span>
                          <span className="text-lg font-bold text-green-600">
                            {adDetails.baseView || "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Financial Details Card */}
                    <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-6 rounded-2xl border border-yellow-200">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-yellow-500 text-white rounded-lg p-2">
                          <FaMoneyBillWave className="w-4 h-4" />
                        </div>
                        <div>
                          <h5 className="font-semibold text-gray-800">Financial Details</h5>
                          <p className="text-sm text-gray-600">Budget and pricing</p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="bg-white rounded-lg p-3 border border-yellow-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Total Budget</span>
                          <span className="text-2xl font-bold text-yellow-600">
                            ₹{adDetails.totalCost || "0.00"}
                          </span>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-yellow-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Subtotal</span>
                          <span className="text-lg font-bold text-yellow-600">
                            ₹{adDetails.subtotal || "0.00"}
                          </span>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-yellow-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">GST</span>
                          <span className="text-lg font-bold text-yellow-600">
                            ₹{adDetails.gst || "0.00"}
                          </span>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-yellow-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Admin Commission</span>
                          <span className="text-lg font-bold text-yellow-600">
                            ₹{adDetails.adminCommission ? Number(adDetails.adminCommission).toFixed(2) : "0.00"}
                          </span>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-yellow-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Reporter Price</span>
                          <span className="text-lg font-bold text-yellow-600">
                            ₹{adDetails.finalReporterPrice ? Number(adDetails.finalReporterPrice).toFixed(2) : "0.00"}
                          </span>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-yellow-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Commission Rate</span>
                          <span className="text-lg font-bold text-yellow-600">
                            {adDetails.adCommissionRate || 0}%
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Location Details Card */}
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-2xl border border-purple-200">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-purple-500 text-white rounded-lg p-2">
                          <FaMapMarkerAlt className="w-4 h-4" />
                        </div>
                        <div>
                          <h5 className="font-semibold text-gray-800">Location Details</h5>
                          <p className="text-sm text-gray-600">Geographic targeting</p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="bg-white rounded-lg p-3 border border-purple-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">State</span>
                          <span className="font-medium text-gray-900">{adDetails.adState || "N/A"}</span>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-purple-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">City</span>
                          <span className="font-medium text-gray-900">{adDetails.adCity || "N/A"}</span>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-purple-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Pincode</span>
                          <span className="font-medium text-gray-900">{adDetails.pincode || "N/A"}</span>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-purple-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Preference State</span>
                          <span className="font-medium text-gray-900">{adDetails.pfState || "N/A"}</span>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-purple-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Preference Cities</span>
                          <span className="font-medium text-gray-900">
                            {Array.isArray(adDetails.pfCities) ? adDetails.pfCities.join(", ") : adDetails.pfCities || "N/A"}
                          </span>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-purple-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">All States</span>
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${adDetails.allStates ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                            {adDetails.allStates ? 'Yes' : 'No'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Advertiser Information Card */}
                    {adDetails.owner && (
                      <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-6 rounded-2xl border border-indigo-200">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="bg-indigo-500 text-white rounded-lg p-2">
                            <FaUser className="w-4 h-4" />
                          </div>
                          <div>
                            <h5 className="font-semibold text-gray-800">Advertiser Information</h5>
                            <p className="text-sm text-gray-600">Submitted by advertiser</p>
                          </div>
                        </div>
                        <div className="space-y-3">
                          <div className="bg-white rounded-lg p-3 border border-indigo-200">
                            <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Name</span>
                            <span className="font-medium text-gray-900">{adDetails.owner.name || "N/A"}</span>
                          </div>
                          <div className="bg-white rounded-lg p-3 border border-indigo-200">
                            <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Email</span>
                            <span className="font-medium text-gray-900">{adDetails.owner.email || "N/A"}</span>
                          </div>
                          <div className="bg-white rounded-lg p-3 border border-indigo-200">
                            <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Mobile</span>
                            <span className="font-medium text-gray-900">{adDetails.owner.mobile || "N/A"}</span>
                          </div>
                          <div className="bg-white rounded-lg p-3 border border-indigo-200">
                            <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">IINSAF ID</span>
                            <span className="font-medium text-gray-900">{adDetails.owner.iinsafId || "N/A"}</span>
                          </div>
                          <div className="bg-white rounded-lg p-3 border border-indigo-200">
                            <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Organization</span>
                            <span className="font-medium text-gray-900">{adDetails.owner.organization || "N/A"}</span>
                          </div>
                          {adDetails.owner.state && (
                            <div className="bg-white rounded-lg p-3 border border-indigo-200">
                              <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Location</span>
                              <span className="font-medium text-gray-900">
                                {adDetails.owner.city || ""} {adDetails.owner.city && adDetails.owner.state ? ", " : ""}{adDetails.owner.state || ""}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Admin Targeting Card */}
                    <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-2xl border border-orange-200">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-orange-500 text-white rounded-lg p-2">
                          <FaFilter className="w-4 h-4" />
                        </div>
                        <div>
                          <h5 className="font-semibold text-gray-800">Admin Targeting</h5>
                          <p className="text-sm text-gray-600">Targeting configuration</p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="bg-white rounded-lg p-3 border border-orange-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Target User Type</span>
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${adDetails.userType === 'influencer' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                            }`}>
                            {adDetails.userType === 'influencer' ? '🎯 Influencer' : '📰 Reporter'}
                          </span>
                        </div>
                        {adDetails.adminSelectState && adDetails.adminSelectState.length > 0 && (
                          <div className="bg-white rounded-lg p-3 border border-orange-200">
                            <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Admin Selected States</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {adDetails.adminSelectState.map((state, idx) => (
                                <span key={idx} className="px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs">
                                  {state}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {adDetails.adminSelectCities && adDetails.adminSelectCities.length > 0 && (
                          <div className="bg-white rounded-lg p-3 border border-orange-200">
                            <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Admin Selected Cities</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {adDetails.adminSelectCities.slice(0, 5).map((city, idx) => (
                                <span key={idx} className="px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs">
                                  {city}
                                </span>
                              ))}
                              {adDetails.adminSelectCities.length > 5 && (
                                <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                                  +{adDetails.adminSelectCities.length - 5} more
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                        {adDetails.adminSelectPincode && (
                          <div className="bg-white rounded-lg p-3 border border-orange-200">
                            <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Admin Selected Pincode</span>
                            <span className="font-medium text-gray-900">{adDetails.adminSelectPincode}</span>
                          </div>
                        )}
                        {adDetails.reporterId && adDetails.reporterId.length > 0 && (
                          <div className="bg-white rounded-lg p-3 border border-orange-200">
                            <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Targeted Reporters</span>
                            <span className="font-medium text-gray-900">{adDetails.reporterId.length} reporter(s) selected</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Media Section */}
                  {(adDetails.imageUrl || adDetails.videoUrl) && (
                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-6 rounded-2xl border border-gray-200 mb-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-gray-500 text-white rounded-lg p-2">
                          {adDetails.mediaType === "image" ? (
                            <FaFileAlt className="w-4 h-4" />
                          ) : (
                            <FaFileAlt className="w-4 h-4" />
                          )}
                        </div>
                        <div>
                          <h5 className="font-semibold text-gray-800">Advertisement Media</h5>
                          <p className="text-sm text-gray-600">{adDetails.mediaType === "image" ? "Image content" : "Video content"}</p>
                        </div>
                      </div>
                      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                        <div className="flex items-center justify-center min-h-[300px] bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                          {adDetails.mediaType === "image" && adDetails.imageUrl && (
                            <img
                              src={adDetails.imageUrl}
                              alt={adDetails.mediaDescription || "Advertisement"}
                              className="max-w-full h-auto max-h-[280px] rounded-lg shadow-sm border border-gray-200 object-contain"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "/fallback-image.jpg";
                              }}
                            />
                          )}
                          {adDetails.mediaType === "video" && adDetails.videoUrl && (
                            <video
                              src={adDetails.videoUrl}
                              controls
                              className="max-w-full h-auto max-h-[280px] rounded-lg shadow-sm border border-gray-200"
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Timestamps */}
                  <div className="bg-gradient-to-br from-slate-50 to-slate-100 p-6 rounded-2xl border border-slate-200">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="bg-slate-500 text-white rounded-lg p-2">
                        <FaClock className="w-4 h-4" />
                      </div>
                      <div>
                        <h5 className="font-semibold text-gray-800">Timestamps</h5>
                        <p className="text-sm text-gray-600">Important dates and times</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-white rounded-lg p-3 border border-slate-200">
                        <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Created At</span>
                        <span className="font-medium text-gray-900">
                          {adDetails.createdAt ? new Date(adDetails.createdAt).toLocaleString() : "N/A"}
                        </span>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-slate-200">
                        <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Updated At</span>
                        <span className="font-medium text-gray-900">
                          {adDetails.updatedAt ? new Date(adDetails.updatedAt).toLocaleString() : "N/A"}
                        </span>
                      </div>
                      {adDetails.approvedAt && (
                        <div className="bg-white rounded-lg p-3 border border-slate-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Approved At</span>
                          <span className="font-medium text-gray-900">
                            {new Date(adDetails.approvedAt).toLocaleString()}
                          </span>
                        </div>
                      )}
                      {adDetails.acceptBefore && (
                        <div className="bg-white rounded-lg p-3 border border-slate-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Accept Before</span>
                          <span className="font-medium text-gray-900">
                            {new Date(adDetails.acceptBefore).toLocaleString()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="mt-8 flex justify-end gap-4">
                    <button
                      onClick={closeAdDetailsModal}
                      className="px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white rounded-xl font-semibold transition-colors shadow-lg hover:shadow-xl"
                    >
                      Close
                    </button>
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-500">No advertisement details available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ApprovedPost;
